import React from "react";

interface ColumnSkeletonProps {
  column: any;
  forHeader?: boolean;
}

export function ColumnSkeleton({
  column,
  forHeader = false,
}: ColumnSkeletonProps) {
  const columnId = column.id;
  const accessorKey = column.accessorKey;
  const header =
    typeof column.header === "function" ? column.header() : column.header;

  // Extract text content from header if it's a React element
  let headerText = "";
  if (header && typeof header === "object" && "props" in header) {
    // Try to extract the text content from the header div
    headerText = header.props.children || "";
  } else if (typeof header === "string") {
    headerText = header;
  }

  const key = columnId || accessorKey || "";
  const headerLower = headerText.toString().toLowerCase();

  // Check for action columns
  if (key === "actions" || key.includes("action")) {
    return forHeader ? (
      <div className="skeleton h-4 w-8" />
    ) : (
      <div className="flex justify-center gap-1">
        <div className="skeleton size-6 rounded-full" />
      </div>
    );
  }

  // Check for common column patterns based on accessor key or ID
  switch (key) {
    // Selection checkbox column
    case "select":
      return (
        <div className="flex justify-center">
          <div className="skeleton size-5 rounded-sm" />
        </div>
      );

    // Icon columns
    case "setCardType":
    case "icon":
    case "stores":
      return forHeader ? (
        <div className="skeleton size-4 rounded-full" />
      ) : (
        <div className="flex justify-center gap-1">
          <div className="skeleton size-6 rounded-full" />
        </div>
      );

    // Quantity columns
    case "quantity":
      return forHeader ? (
        <div className="skeleton mx-auto h-4 w-8" />
      ) : (
        <div className="skeleton mx-auto h-4 w-4" />
      );

    // Name columns
    case "data.name":
    case "name":
      return forHeader ? (
        <div className="skeleton h-4 w-10" />
      ) : (
        <div className="skeleton h-5 w-36" />
      );

    // Set code columns
    case "data.setCode":
    case "setCode":
      return forHeader ? (
        <div className="skeleton mx-auto h-4 w-8 rounded-full" />
      ) : (
        <div className="skeleton mx-auto size-8 rounded-full" />
      );
    case "code":
      return forHeader ? (
        <div className="skeleton h-4 w-8 rounded-full" />
      ) : (
        <div className="skeleton h-5 w-12 rounded-full" />
      );

    // Card count columns
    case "totalSetSize":
    case "cardCount":
      return <div className="skeleton h-4 w-8" />;

    // Price columns
    case "data.price":
    case "price":
    case "purchasePrice":
      return forHeader ? (
        <div className="skeleton mx-auto h-4 w-12" />
      ) : (
        <div className="skeleton mx-auto h-4 w-16" />
      );
    case "marketCap":
      return <div className="skeleton h-4 w-16" />;
    case "setCardPrice":
    case "priceChange":
      return <div className="skeleton h-4 w-12" />;

    // Finishes/Foil columns
    case "finishes":
      return forHeader ? (
        <div className="skeleton mx-auto h-4 w-8 rounded-full" />
      ) : (
        <div className="skeleton mx-auto h-6 w-6 rounded-full" />
      );

    // Language columns
    case "language":
      return forHeader ? (
        <div className="skeleton mx-auto h-4 w-7 rounded-md" />
      ) : (
        <div className="skeleton mx-auto h-5 w-7 rounded-md" />
      );

    // Condition columns
    case "condition":
      return forHeader ? (
        <div className="skeleton mx-auto h-4 w-10 rounded-md" />
      ) : (
        <div className="skeleton mx-auto h-5 w-10 rounded-md" />
      );

    // Date columns
    case "createdAt":
    case "updatedAt":
    case "releaseDate":
      return <div className="skeleton h-4 w-16" />;

    // Number columns
    case "number":
      return <div className="skeleton h-4 w-8" />;

    // Mana cost columns
    case "costPrinted":
      return forHeader ? (
        <div className="skeleton h-4 w-12" />
      ) : (
        <div className="flex justify-start gap-1">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="skeleton h-4 w-4 rounded-full" />
          ))}
        </div>
      );

    // Type columns
    case "type":
      return <div className="skeleton size-8 rounded-full" />;

    // Rarity columns
    case "rarity":
      return <div className="skeleton h-4 w-12" />;

    // Rating columns (example of custom column)
    case "rating":
      return forHeader ? (
        <div className="skeleton h-4 w-12 rounded-md" />
      ) : (
        <div className="flex justify-center gap-1">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="skeleton h-4 w-4 rounded-full" />
          ))}
        </div>
      );
  }

  // Check based on header text for columns without specific IDs
  if (headerLower.includes("qty") || headerLower.includes("cards")) {
    return forHeader ? (
      <div className="skeleton h-4 w-8" />
    ) : (
      <div className="skeleton mx-auto h-4 w-6" />
    );
  }

  if (headerLower.includes("price") || headerLower.includes("cap")) {
    return forHeader ? (
      <div className="skeleton h-4 w-12" />
    ) : (
      <div className="skeleton mx-auto h-4 w-16" />
    );
  }

  if (headerLower.includes("foil") || headerLower.includes("finishes")) {
    return forHeader ? (
      <div className="skeleton h-4 w-8 rounded-full" />
    ) : (
      <div className="skeleton mx-auto h-4 w-6 rounded-full" />
    );
  }

  if (headerLower.includes("lang")) {
    return forHeader ? (
      <div className="skeleton h-4 w-6 rounded-md" />
    ) : (
      <div className="skeleton mx-auto h-4 w-8 rounded-md" />
    );
  }

  if (headerLower.includes("cond")) {
    return forHeader ? (
      <div className="skeleton h-4 w-8 rounded-md" />
    ) : (
      <div className="skeleton mx-auto h-5 w-8 rounded-md" />
    );
  }

  if (
    headerLower.includes("date") ||
    headerLower.includes("added") ||
    headerLower.includes("modified") ||
    headerLower.includes("updated") ||
    headerLower.includes("released")
  ) {
    return forHeader ? (
      <div className="skeleton h-4 w-12" />
    ) : (
      <div className="skeleton mx-auto h-4 w-10" />
    );
  }

  if (headerLower.includes("type")) {
    return forHeader ? (
      <div className="skeleton mx-auto h-5 w-8 rounded-md" />
    ) : (
      <div className="skeleton mx-auto h-5 w-8 rounded-md" />
    );
  }

  if (headerLower.includes("user") || headerLower.includes("contact")) {
    return forHeader ? (
      <div className="skeleton h-4 w-8 rounded-full" />
    ) : (
      <div className="skeleton mx-auto size-6 rounded-full" />
    );
  }

  if (headerLower.includes("code")) {
    return forHeader ? (
      <div className="skeleton h-4 w-8" />
    ) : (
      <div className="skeleton h-4 w-12" />
    );
  }

  // Default skeleton for any other column types
  return forHeader ? (
    <div className="skeleton h-4 w-8" />
  ) : (
    <div className="skeleton mx-auto h-4 w-16" />
  );
}
